import { Injectable } from '@angular/core';
import { COLUMN_SEPARATORS, DECIMAL_SEPARATORS, ENVELOPE_CURVE_CONSTANTS } from '../constants/envelope-curve.constants';

export interface ParsedPoint {
  x: number;
  y: number;
}

@Injectable({ providedIn: 'root' })
export class ExcelParserService {
  parseExcelData(text: string): ParsedPoint[] {
    const lines = text
      .trim()
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean);

    if (lines.length === 0) return [];

    const dataLines = this.skipHeaderIfPresent(lines);
    const separator = this.detectColumnSeparator(dataLines[0] ?? '');

    return dataLines
      .map(line => this.parseLine(line, separator))
      .filter((p): p is ParsedPoint => p !== null)
      .slice(0, ENVELOPE_CURVE_CONSTANTS.MAX_POINTS);
  }

  private skipHeaderIfPresent(lines: string[]): string[] {
    const firstLine = lines[0]?.toLowerCase() ?? '';
    const hasHeader = (firstLine.includes('x') || firstLine.includes('y')) && !/^\d/.test(firstLine);
    return hasHeader ? lines.slice(1) : lines;
  }

  private detectColumnSeparator(line: string): string {
    if (line.includes(COLUMN_SEPARATORS.TAB)) return COLUMN_SEPARATORS.TAB;
    if (line.includes(COLUMN_SEPARATORS.SEMICOLON)) return COLUMN_SEPARATORS.SEMICOLON;
    return COLUMN_SEPARATORS.COMMA;
  }

  private parseLine(line: string, separator: string): ParsedPoint | null {
    const parts = line.split(separator).map(p => p.trim());
    if (parts.length < 2) return null;

    const x = this.parseNumber(parts[0] ?? '');
    const y = this.parseNumber(parts[1] ?? '');

    if (!this.isValidNumber(x) || !this.isValidNumber(y)) return null;
    if (!this.isInRange(x) || !this.isInRange(y)) return null;

    return { x, y };
  }

  private parseNumber(value: string): number {
    const normalized = value.replace(DECIMAL_SEPARATORS.COMMA, DECIMAL_SEPARATORS.POINT);
    return parseFloat(normalized);
  }

  private isValidNumber(value: number): boolean {
    return !isNaN(value);
  }

  private isInRange(value: number): boolean {
    return value >= ENVELOPE_CURVE_CONSTANTS.MIN_VALUE && value <= ENVELOPE_CURVE_CONSTANTS.MAX_VALUE;
  }
}
